import React from 'react';
import { useSEO } from '../hooks/useSEO';
import { Link } from 'react-router-dom';
import { Building2, CreditCard, Globe, Copy, ArrowUpRight, Shield } from 'lucide-react';
import { toast } from 'sonner';
import PageHero from '../components/PageHero';
import { BANK_INFO } from '../data/mock';
import { usePublicBankAccounts } from '../hooks/usePublicSettings';
import { EditRegion } from '../components/AdminEditBar';
import { InlineText } from '../components/InlineEditable';

const CHANNELS = [
  {
    key: 'card',
    icon: CreditCard,
    titleKey: 'channels_card_title',
    titleFallback: 'Kredi Kartı / Online',
    textKey: 'channels_card_text',
    textFallback: "Hızlı, güvenli, 7/24. Türkiye'nin tüm bankalarının kartlarıyla tek çekim veya taksit.",
    ctaKey: 'channels_card_cta',
    ctaFallback: 'Online Bağış',
    link: '/bagis',
  },
  {
    key: 'eft',
    icon: Building2,
    titleKey: 'channels_eft_title',
    titleFallback: 'Havale / EFT',
    textKey: 'channels_eft_text',
    textFallback: 'Vakfımıza ait banka hesap numaraları aşağıdadır. Açıklama kısmına adınızı yazmanızı rica ederiz.',
    ctaKey: 'channels_eft_cta',
    ctaFallback: "IBAN'ları Gör",
    link: '#banka',
  },
  {
    key: 'intl',
    icon: Globe,
    titleKey: 'channels_intl_title',
    titleFallback: 'Uluslararası Bağış',
    textKey: 'channels_intl_text',
    textFallback: 'Yurt dışından SWIFT kodu ile bağış yapabilirsiniz.',
    ctaKey: 'channels_intl_cta',
    ctaFallback: 'Uluslararası',
    link: '#intl',
  },
];

export default function DonationChannels() {
  useSEO('donation_channels');
  const banksFromDb = usePublicBankAccounts();
  const banks = banksFromDb.length > 0 ? banksFromDb : BANK_INFO;
  // TL accounts for the "Havale & EFT" section, foreign-currency accounts for the SWIFT section
  const trBanks = banks.filter(b => !b.currency || String(b.currency).toUpperCase() === 'TRY' || String(b.currency).toUpperCase() === 'TL');
  const intlBanks = banks.filter(b => {
    const c = String(b.currency || '').toUpperCase();
    return c && c !== 'TRY' && c !== 'TL';
  });

  const copy = async (iban) => { await navigator.clipboard.writeText(iban); toast.success('IBAN kopyalandı'); };

  return (
    <EditRegion label="Bağış Kanalları" to="/admin/bank-accounts">
      <>
      <PageHero slug="donation_channels"
        kicker="Bağış Kanallarımız"
        title={<>Her kanal, aynı <span className="italic text-coral-200">etki</span>.</>}
        text="YEKÜV'e size en uygun kanaldan bağış yapabilirsiniz. Her bağış makbuzla belgelenir."
      />

      <section className="bg-cream">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 md:py-28">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="donation-channels-cards">
            {CHANNELS.map((c) => {
              const Icon = c.icon;
              return (
                <div key={c.key} className="group bg-white rounded-3xl p-8 border border-ink-100 hover:border-gold transition-all hover:-translate-y-1">
                  <div className="w-14 h-14 rounded-2xl bg-ink-50 grid place-items-center text-ink-600 group-hover:bg-ink-600 group-hover:text-gold transition-colors"><Icon className="w-6 h-6" /></div>
                  <InlineText settingKey={c.titleKey} fallback={c.titleFallback} as="h3"
                    className="font-display text-2xl mt-5 text-ink-600 leading-tight" />
                  <InlineText settingKey={c.textKey} fallback={c.textFallback} as="p"
                    multiline className="mt-2 text-sm text-ink-500 leading-relaxed" />
                  {c.link.startsWith('/') ? (
                    <Link to={c.link} className="mt-5 inline-flex items-center gap-2 text-ink-600 text-sm font-medium group-hover:gap-3 transition-all">
                      <InlineText settingKey={c.ctaKey} fallback={c.ctaFallback} as="span" /> <ArrowUpRight className="w-4 h-4" />
                    </Link>
                  ) : (
                    <a href={c.link} className="mt-5 inline-flex items-center gap-2 text-ink-600 text-sm font-medium group-hover:gap-3 transition-all">
                      <InlineText settingKey={c.ctaKey} fallback={c.ctaFallback} as="span" /> <ArrowUpRight className="w-4 h-4" />
                    </a>
                  )}
                </div>
              );
            })}
          </div>

          <div id="banka" className="mt-20">
            <div className="flex items-end justify-between gap-6 mb-10">
              <div>
                <InlineText settingKey="channels_iban_kicker" fallback="IBAN / Hesap Bilgileri" as="div"
                  className="text-[11px] tracking-[0.3em] uppercase text-coral-300 font-semibold" />
                <InlineText settingKey="channels_iban_title" fallback="Havale & EFT" as="h2"
                  className="font-display text-4xl md:text-5xl mt-3 text-ink-600 leading-[1.05]" />
              </div>
              <Shield className="w-8 h-8 text-gold" />
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="bank-accounts-list">
              {trBanks.map((b, i) => (
                <EditRegion key={b.id || i} label="Banka Hesabını Düzenle" to="/admin/bank-accounts">
                  <div className="bg-white rounded-3xl p-7 border border-ink-100" data-testid={`bank-card-${i}`}>
                    <div className="flex items-center justify-between">
                      <div className="font-display text-xl text-ink-600">{b.bank}</div>
                      <span className="text-[10px] font-semibold uppercase tracking-widest bg-gold/20 text-gold-500 rounded-full px-2.5 py-1">{b.currency}</span>
                    </div>
                    {b.branch && <div className="text-xs text-ink-400 mt-1">{b.branch}</div>}
                    <div className="mt-5 p-4 rounded-xl bg-cream-200 font-mono text-sm text-ink-600 tabular break-all">{b.iban}</div>
                    <button onClick={() => copy(b.iban)} className="mt-3 inline-flex items-center gap-2 text-xs text-ink-500 hover:text-ink-600 font-medium" data-testid={`copy-iban-${i}`}><Copy className="w-3.5 h-3.5" /> IBAN Kopyala</button>
                  </div>
                </EditRegion>
              ))}
            </div>
            <div className="mt-6 p-5 rounded-2xl bg-coral-50 border border-coral-100 text-coral-400 text-sm">
              <InlineText settingKey="channels_iban_note"
                fallback="Önemli: Havale açıklamasına adınız ve mümkünse e-posta adresinizi yazmanız, makbuzunuzun size ulaşması için gereklidir."
                as="span" multiline />
            </div>
          </div>

          <div id="intl" className="mt-20 max-w-[1100px] mx-auto">
            <div className="text-center mb-8">
              <Globe className="w-8 h-8 text-ink-600 mx-auto" />
              <InlineText settingKey="channels_swift_title" fallback="Uluslararası Bağış (SWIFT)" as="h3"
                className="font-display text-3xl mt-4 text-ink-600" />
              <InlineText settingKey="channels_swift_text" fallback="Yurt dışından SWIFT kodu ile bağış yapabilirsiniz." as="p"
                multiline className="mt-3 text-ink-500 max-w-xl mx-auto" />
            </div>
            {intlBanks.length === 0 ? (
              <div className="text-center text-ink-400 text-sm">Henüz uluslararası hesap eklenmedi. Admin panelden Banka Hesapları'na EUR/USD hesabı ekleyin.</div>
            ) : (
              <div className={`grid gap-5 ${intlBanks.length >= 2 ? 'md:grid-cols-2' : 'md:grid-cols-1 max-w-[560px] mx-auto'}`} data-testid="intl-bank-accounts-list">
                {intlBanks.map((b, i) => {
                  const cur = String(b.currency || '').toUpperCase();
                  const label = cur === 'EUR' ? 'EURO HESABI' : cur === 'USD' ? 'DOLAR HESABI' : `${cur} HESABI`;
                  return (
                    <EditRegion key={b.id || i} label="Banka Hesabını Düzenle" to="/admin/bank-accounts">
                      <div className="bg-white rounded-3xl p-8 border border-ink-100 h-full" data-testid={`intl-bank-card-${i}`}>
                        <div className="flex items-center justify-between">
                          <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-coral-300">{label}</div>
                          <span className="text-[10px] font-semibold uppercase tracking-widest bg-gold/20 text-gold-500 rounded-full px-2.5 py-1">{cur}</span>
                        </div>
                        <div className="font-display text-xl text-ink-600 mt-3">{b.bank || 'Türkiye İş Bankası'}</div>
                        {b.account_holder && <div className="text-sm text-ink-500 mt-1">{b.account_holder}</div>}
                        <div className="mt-5 space-y-2 text-sm">
                          <div>
                            <div className="text-[10px] uppercase tracking-widest text-ink-400 font-semibold mb-1">IBAN</div>
                            <div className="flex items-center gap-2">
                              <div className="flex-1 p-3 rounded-xl bg-cream-200 font-mono text-ink-600 text-xs tabular break-all">{b.iban}</div>
                              <button onClick={() => copy(b.iban)} className="h-9 w-9 grid place-items-center rounded-xl bg-cream-200 hover:bg-cream-300 text-ink-500" title="IBAN Kopyala" data-testid={`copy-intl-iban-${i}`}><Copy className="w-4 h-4" /></button>
                            </div>
                          </div>
                          {b.swift && (
                            <div>
                              <div className="text-[10px] uppercase tracking-widest text-ink-400 font-semibold mb-1">SWIFT Kodu</div>
                              <div className="p-3 rounded-xl bg-cream-200 font-mono text-ink-600 text-sm">{b.swift}</div>
                            </div>
                          )}
                        </div>
                      </div>
                    </EditRegion>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </section>
    </>
    </EditRegion>
  );
}
